// src/hooks/useProducts.js
import { useState, useEffect, useCallback } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// ─── Categories ─────────────────────────────────────────────────────────────

// RESTORED: withImages is back in the arguments object
export function useCategories({ withCounts = false, withImages = false } = {}) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    // Dynamic parameter building with cache-busting to ensure fresh stock calculations
    const params = new URLSearchParams();
    if (withCounts) params.set('counts', '1');
    if (withImages) params.set('images', '1');
    params.set('_t', Date.now()); // Appends unique timestamp

    fetch(`${API_URL}/categories/list.php?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.success) setCategories(data.categories);
        else setError(data.error || 'Failed to load categories');
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [withCounts, withImages]); // Make sure withImages stays in the dependency array

  return { categories, loading, error };
}

// ─── Products list ───────────────────────────────────────────────────────────

export function useProducts({
  category    = null,
  featured    = false,
  newArrivals = false,
  search      = null,
  page        = 1,
  limit       = 12,
} = {}) {
  const [products, setProducts] = useState([]);
  const [total, setTotal]       = useState(0);
  const [pages, setPages]       = useState(1);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (category)    params.set('category', category);
    if (featured)     params.set('featured', '1');
    if (newArrivals)  params.set('new_arrivals', '1');
    if (search)       params.set('search', search);
    params.set('page',  page);
    params.set('limit', limit);
    // Cache-bust: ensures stock_qty and out_of_stock are always fresh
    params.set('_t', Date.now());

    fetch(`${API_URL}/products/list.php?${params}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setProducts(data.products);
          setTotal(data.total);
          setPages(data.pages);
        } else {
          setError(data.error || 'Failed to load products');
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [category, featured, newArrivals, search, page, limit]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return { products, total, pages, loading, error, refetch: fetchProducts };
}

// ─── Single product ──────────────────────────────────────────────────────────

export function useProduct(id) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Cache-bust single product fetch too
    fetch(`${API_URL}/products/get.php?id=${encodeURIComponent(id)}&_t=${Date.now()}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.success) setProduct(data.product);
        else setError(data.error || 'Product not found');
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [id]);

  return { product, loading, error };
}